import React, { useState, useEffect } from "react"
import { StatisticsManager, IOverallStats, IDailyGameStats } from "../statisticsManager"
import strings from "../strings"

interface StatisticsProps {
  isVisible: boolean
  onClose: () => void
}

function Statistics({ isVisible, onClose }: StatisticsProps) {
  const [overallStats, setOverallStats] = useState<IOverallStats | null>(null)
  const [recentStats, setRecentStats] = useState<IDailyGameStats[]>([])

  useEffect(() => {
    if (isVisible) {
      loadStatistics()
    }
  }, [isVisible])

  const loadStatistics = () => {
    try {
      const overall = StatisticsManager.getOverallStats()
      const recent = StatisticsManager.getRecentStats(7)
      
      setOverallStats(overall)
      setRecentStats(recent)
    } catch (error) {
      console.error(strings.errors.loadingStatistics, error)
    }
  }

  const getGuessDistributionMax = () => {
    if (!overallStats) return 1
    const values = Object.values(overallStats.guessDistribution)
    return Math.max(...values, 1)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', strings.shortDateFormat)
  }

  if (!isVisible || !overallStats) {
    return null
  }

  return (
    <div className="statistics-overlay">
      <div className="statistics-modal">
        <div className="statistics-header">
          <h2>{strings.closeStatistics}</h2>
          <button 
            className="close-button" 
            onClick={onClose}
            aria-label={strings.a11y.closeStatistics}
          >
            {strings.dismissNotification}
          </button>
        </div>

        <div className="statistics-content">
          {/* Overall Statistics */}
          <div className="stats-section">
            <h3>{strings.overallPerformance}</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-number">{overallStats.gamesPlayed}</div>
                <div className="stat-label">{strings.played}</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{overallStats.winPercentage}%</div>
                <div className="stat-label">{strings.winRate}</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{overallStats.currentStreak}</div>
                <div className="stat-label">{strings.currentStreak}</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{overallStats.maxStreak}</div>
                <div className="stat-label">{strings.maxStreak}</div>
              </div>
            </div>
          </div>

          {/* Guess Distribution */}
          {overallStats.gamesWon > 0 && (
            <div className="stats-section">
              <h3>{strings.guessDistribution}</h3>
              <div className="guess-distribution">
                {[1, 2, 3, 4, 5, 6].map(attempt => {
                  const count = overallStats.guessDistribution[attempt] || 0
                  const maxCount = getGuessDistributionMax()
                  const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0
                  
                  return (
                    <div key={attempt} className="distribution-row">
                      <div className="attempt-number">{attempt}</div>
                      <div className="distribution-bar">
                        <div 
                          className="distribution-fill"
                          style={{ width: `${Math.max(percentage, count > 0 ? 10 : 0)}%` }}
                        >
                          {count > 0 && <span className="count-label">{count}</span>}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
              {overallStats.averageGuesses > 0 && (
                <p className="average-guesses">
                  {strings.averageGuesses(overallStats.averageGuesses)}
                </p>
              )}
            </div>
          )}

          {/* Recent Games */}
          {recentStats.length > 0 && (
            <div className="stats-section">
              <h3>{strings.recentGames} ({strings.lastSevenDays})</h3>
              <div className="recent-games">
                {recentStats.map(stat => (
                  <div key={stat.date} className="recent-game">
                    <div className="game-date">{formatDate(stat.date)}</div>
                    <div className={`game-result ${stat.won ? 'won' : 'lost'}`}>
                      {stat.won ? `✓ ${stat.attempts}` : '✗'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Games Message */}
          {overallStats.gamesPlayed === 0 && (
            <div className="no-games-message">
              <p>{strings.noGamesPlayed}</p>
              <p>{strings.completeFirstGame}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Statistics